// Focus containment for the mobile nav drawer.
//
// Split out of NebariNavDrawer, which outgrew its line cap once the drawer
// gained its persona filter — and the seam is a real one: this is the whole
// keyboard contract in one place.

import { useEffect, type RefObject } from 'react';

import { useModalLayer } from './useModalLayer';

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Moves focus into `panelRef` once, then keeps Tab inside it and closes on
 * Escape.
 *
 * The initial focus is deliberately its own effect, keyed only on the ref —
 * whose identity never changes, so it runs once per open. Keyed on `onClose`
 * it re-fired whenever anything above the drawer re-rendered, dragging focus
 * back to the first row mid-interaction.
 */
export function useDrawerFocusTrap(panelRef: RefObject<HTMLElement>, onClose: () => void) {
  // The search palette can open over the drawer, and it binds Escape on
  // `window` too. Without this the drawer answered the same press and closed
  // underneath it.
  const { isTopmost } = useModalLayer('nav-drawer');

  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
  }, [panelRef]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!isTopmost()) return;
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // Keep Tab inside the drawer: it covers the viewport, so letting focus
      // reach the page behind it would strand the keyboard user off-screen.
      const panel = panelRef.current;
      if (e.key !== 'Tab' || !panel) return;
      const stops = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (stops.length === 0) return;
      const edge = e.shiftKey ? stops[0] : stops[stops.length - 1];
      if (document.activeElement === edge) {
        e.preventDefault();
        (e.shiftKey ? stops[stops.length - 1] : stops[0]).focus();
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panelRef, onClose, isTopmost]);
}
